import React, { useState, useEffect, useImperativeHandle } from 'react';

/**
 * LHCFormEditor - Editor for LHC form schemas (admin use)
 * Provides file upload and JSON textarea for editing LHC form definitions
 *
 * @param {Object} schema - Initial schema object
 * @param {Function} onChange - Callback when schema changes
 * @param {Function} onError - Callback for error handling
 * @param {React.Ref} ref - Ref to expose getSchema method
 */
const LHCFormEditor = React.forwardRef(({ schema, onChange, onError }, ref) => {
  const [schemaJson, setSchemaJson] = useState('');
  const [currentSchema, setCurrentSchema] = useState({});

  // Expose getSchema method to parent component
  useImperativeHandle(ref, () => ({
    getSchema: () => {
      return currentSchema;
    }
  }), [currentSchema]);

  // Initialize schemaJson from schema prop
  useEffect(() => {
    if (schema && Object.keys(schema).length > 0) {
      setSchemaJson(JSON.stringify(schema, null, 2));
      setCurrentSchema(schema);
    }
  }, [schema]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonSchema = JSON.parse(event.target.result);
          setSchemaJson(JSON.stringify(jsonSchema, null, 2));
          setCurrentSchema(jsonSchema);
          onChange(jsonSchema);
        } catch (err) {
          if (onError) {
            onError('Invalid JSON file');
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const handleJsonChange = (e) => {
    setSchemaJson(e.target.value);
    try {
      const jsonSchema = JSON.parse(e.target.value);
      setCurrentSchema(jsonSchema);
      onChange(jsonSchema);
    } catch (err) {
      // Don't update schema if JSON is invalid (user is still typing)
    }
  };

  return (
    <div className="card p-3 mb-3">
      <h5 className="mb-3">LHC Form Schema Editor</h5>

      <div className="mb-3">
        <label htmlFor="lhcSchemaFile" className="form-label">
          Upload LHC JSON File
        </label>
        <input
          type="file"
          className="form-control"
          id="lhcSchemaFile"
          accept=".json"
          onChange={handleFileChange}
        />
      </div>

      <div className="text-center text-muted my-2">
        <small>── OR ──</small>
      </div>

      <div className="mb-3">
        <label htmlFor="lhcSchemaJson" className="form-label">
          Edit JSON Schema Directly
        </label>
        <textarea
          className="form-control font-monospace"
          id="lhcSchemaJson"
          value={schemaJson}
          onChange={handleJsonChange}
          rows="15"
          placeholder="Paste LHC form schema here"
        ></textarea>
      </div>

      <div className="text-center text-muted my-2">
        <small>LHC forms can be created/edited here:<a href="https://formbuilder.nlm.nih.gov" target="_blank" rel="noreferrer">NIH Form Builder</a></small>
      </div>

    </div>
  );
});

LHCFormEditor.displayName = 'LHCFormEditor';

export default LHCFormEditor;
